import { Fragment } from 'react';
import classNames from 'classnames';

import { Icon } from '@/components/icon';
import {
  ChatMessage,
  ChatUserMessage,
  ChatMode,
  ChatUserQuestion,
  ConversationJob,
} from '@/explore/types';
import { isUserQuestionMessage } from '@/core/cell';

import { UserMessageInput } from './user-message-input';
import { InitialMessage } from './initial-message';
import { UserMessage } from './user-message';
import { JobTree } from './job-tree';

import styles from '../chat-cell.module.scss';

interface ChatMessagesProps {
  messages: ChatMessage[];
  allUserMessages?: ChatUserQuestion[];
  pendingFirstInput?: boolean;
  editableInitialMessage?: boolean;
  draft?: string;
  disabled?: boolean;
  jobs: ConversationJob[];
  onAccept: (partial?: boolean) => void;
  onDiscard: (partial?: boolean) => void;
  onSubmit(answer: string, sources: string[], mode: ChatMode): void;
  onCancel: () => void;
  onMessageChange: (message: ChatUserMessage) => void;
  onRetry: () => void;
  onAbort: (jobId: string) => void;
}

export const ChatMessages = (props: ChatMessagesProps) => {
  const {
    messages,
    allUserMessages,
    pendingFirstInput = false,
    editableInitialMessage = false,
    disabled = false,
    draft,
    jobs,
    onAccept,
    onDiscard,
    onSubmit,
    onCancel,
    onMessageChange,
    onRetry,
    onAbort,
  } = props;

  const [firstMessage] = messages;
  const [lastMessage] = messages.slice(-1);
  const lastUserMessage = allUserMessages?.at(-1);

  const handleAnswer = (answer: string, selectedSources: string[], mode: ChatMode) => {
    if (!answer.length) {
      return onAccept(true);
    }

    onSubmit(answer, selectedSources, mode);
  };

  if (!messages.length || (editableInitialMessage && firstMessage?.type === 'followup_question')) {
    const sources =
      firstMessage !== undefined && isUserQuestionMessage(firstMessage)
        ? firstMessage.sources
        : lastUserMessage?.sources;

    return (
      <div className={styles.form}>
        <UserMessageInput
          placeholder="Follow up or request edits"
          initialValue={messages.length > 0 ? (firstMessage?.message ?? draft) : undefined}
          sources={sources}
          mode="normal"
          disabled={disabled}
          onSubmit={handleAnswer}
          onCancel={onCancel}
          onAccept={() => onAccept(true)}
          primaryActions={(value, sources, mode) =>
            !disabled && (pendingFirstInput || value.length > 0)
              ? [
                  {
                    label: 'Ask',
                    disabled: sources.length === 0,
                    onClick: () => handleAnswer(value, sources ?? [], mode),
                  },
                ]
              : [
                  { label: 'Accept last', onClick: () => onAccept(true) },
                  { label: 'Accept all blocks', onClick: () => onAccept() },
                ]
          }
          secondaryActions={
            pendingFirstInput
              ? [
                  {
                    label: 'Cancel',
                    onClick: (e) => {
                      e.stopPropagation();
                      onDiscard();
                    },
                  },
                ]
              : [
                  {
                    label: 'Discard last',
                    onClick: (e) => {
                      e.stopPropagation();
                      onDiscard();
                    },
                  },
                  {
                    label: 'Discard all blocks',
                    onClick: (e) => {
                      e.stopPropagation();
                      onDiscard(false);
                    },
                  },
                ]
          }
          autoFocus
        />
      </div>
    );
  }

  const hasAnswer = (idx: number) =>
    steps.some((step, i) => i > idx && step.type === 'clarifying_answer');

  const steps = firstMessage.role === 'user' ? messages.slice(1) : messages;

  return (
    <div className={styles.form}>
      {firstMessage.role === 'user' && (
        <InitialMessage
          editable={editableInitialMessage}
          initialValue={draft}
          message={firstMessage}
          disabled={disabled}
          onChange={onMessageChange}
          onDiscard={onDiscard}
        />
      )}
      <div className={styles.steps}>
        {steps.length > 0 && (
          <>
            {steps.map((step, index) => {
              switch (step.type) {
                case 'clarifying_question':
                  return (
                    <Fragment key={index}>
                      {hasAnswer(index) ? null : (
                        <div
                          className={classNames([styles.step, styles.clarifying, styles.fadeIn])}>
                          <Icon name="PauseIcon" size={16} className={styles.pauseIcon} />
                          Clarifying user intent
                        </div>
                      )}
                      <div className={classNames(styles.questionContainer, styles.fadeIn)}>
                        <div className={styles.avatar}>
                          <Icon name="Zap" size={24} />
                        </div>
                        <div className={styles.content}>
                          <div className={styles.clarificationHeader}>Clarifying question</div>
                          <div className={styles.question}>{step.message}</div>
                        </div>
                      </div>
                    </Fragment>
                  );
                case 'clarifying_answer':
                  return (
                    <UserMessage
                      disabled={disabled}
                      key={step.id}
                      message={step}
                      className={styles.fadeIn}
                      onChange={onMessageChange}
                    />
                  );
                case 'followup_question':
                case 'initial_user_prompt':
                  throw new Error('Unhandled follow-up chat flow');
              }
            })}
          </>
        )}

        <JobTree jobs={jobs} onRetry={onRetry} onAbort={onAbort} />

        {lastMessage.type === 'clarifying_question' && (
          <UserMessageInput
            initialValue={draft}
            placeholder={
              disabled
                ? 'Legacy conversations cannot be continued'
                : 'Reply to the clarifying question'
            }
            sources={lastUserMessage?.sources}
            autoFocus
            disabled={disabled}
            onSubmit={handleAnswer}
            primaryActions={(value, sources, mode) =>
              disabled
                ? []
                : [
                    {
                      label: 'Answer',
                      disabled: value.length === 0 || sources.length === 0,
                      onClick: () => handleAnswer(value, sources, mode),
                    },
                  ]
            }
            secondaryActions={[{ label: 'Cancel', onClick: onCancel }]}
          />
        )}
      </div>
    </div>
  );
};
